import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { FaEdit } from "react-icons/fa";

// Components
import CustomButton from "../../components/common/CustomButton/CustomButton.tsx";
import { Column } from "../../components/common/CustomDataTable/CustomDataTable.tsx";
import { CustomTabPanel, SubHeaderTitle } from "../../components/common/index";
import { CommonData } from "../PageImports.ts";
import ErrorPage from "../Error/ErrorPage.tsx";
import Loader from "../../utils/Loader.tsx";

// Utilities
import { axiosFunction } from "../../components/commonFunctions/axiosFunction.ts";
import { v2ReactURL } from "../../utils/constant.ts";
import {
  CheckedValue,
  debounce,
  ErrorState,
  Pagination,
} from "../Contact/MainDataTable.tsx";

const {
  Wrapper,
  WrapperSubHeader,
  WrapperContent,
  WrapperCard,
  Row,
  CustomDataTable,
} = CommonData;

// const { toastMessage } = CommonFunctions;

interface DeliverySlotProps {
  navUrl: string;
}

interface DeliverySlotData {
  deliverySlotId: number;
  deliverySlotName: string;
  deliverySlotType: string;
  startTime: string;
  endTime: string;
  cutOffHours: number;
  maximumOrders: number;
  calculatedCutOffTime: string;
  isActive: number;
}

interface DeliverySlotFormData {
  deliverySlotId: number;
  deliverySlotName: string;
  deliverySlotType: string;
  startTime: string;
  endTime: string;
  cutOffHours: number;
  maximumOrders: number;
  calculatedCutOffTime: string;
  isActive: number;
}

interface UserPermissions {
  canView: boolean;
  canInsert: boolean;
  canEdit: boolean;
  canDelete: boolean;
  loading: boolean;
  error: string | null;
}

interface TimeValidation {
  startTimeError: string;
  endTimeError: string;
  slotNameError: string;
  validating: boolean;
}

const DeliverySlot: React.FC<DeliverySlotProps> = ({ navUrl }) => {
  const tabView = navUrl;

  // Permission state
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({
    canView: false,
    canInsert: false,
    canEdit: false,
    canDelete: false,
    loading: true,
    error: null,
  });

  const [selectedDeliverySlot, setSelectedDeliverySlot] =
    useState<DeliverySlotData | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<DeliverySlotData[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    pages: 0,
    perpage: 0,
    total: 0,
  });
  const [pageOption, setPageOption] = useState<string>("15");
  const [searchValue, setSearchValue] = useState<string>("");
  const [error, setError] = useState<ErrorState | null>(null);
  const [checkedValue, setCheckedValue] = useState<CheckedValue>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [deliverySlotType, setDeliverySlotType] = useState<string>("General");

  // Form state
  const [formData, setFormData] = useState<DeliverySlotFormData>({
    deliverySlotId: 0,
    deliverySlotName: "",
    deliverySlotType: "General",
    startTime: "",
    endTime: "",
    cutOffHours: 0,
    maximumOrders: 0,
    calculatedCutOffTime: "",
    isActive: 1,
  });

  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [timeValidation, setTimeValidation] = useState<TimeValidation>({
    startTimeError: "",
    endTimeError: "",
    slotNameError: "",
    validating: false,
  });

  // Snackbar state for toast messages
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
    vertical: "bottom" as "top" | "bottom",
    horizontal: "right" as "left" | "center" | "right",
  });

  // Show snackbar function
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "success",
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
      vertical: "bottom",
      horizontal: "right",
    });
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Override toastMessage function to use our custom snackbar
  const customToastMessage = (
    message: string,
    type: "success" | "error" | "warning" | "info" = "success",
  ) => {
    showSnackbar(message, type);
  };

  console.log("showValidation", showValidation);

  // API validation functions
  const checkStartTime = async (
    deliverySlotId: number,
    startTime: string,
    updateStartTime: string = "0",
  ) => {
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/checkStartTime`,
        "POST",
        {
          deliverySlotId: deliverySlotId || 0,
          startTime: startTime,
          updateStartTime: updateStartTime,
        },
        false,
      );

      return response.data;
    } catch (error: any) {
      console.error("Error checking start time:", error);
      throw error;
    }
  };

  const checkEndTime = async (
    deliverySlotId: number,
    endTime: string,
    updateEndTime: string = "0",
  ) => {
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/checkEndTime`,
        "POST",
        {
          deliverySlotId: deliverySlotId || 0,
          endTime: endTime,
          updateEndTime: updateEndTime,
        },
        false,
      );

      return response.data;
    } catch (error: any) {
      console.error("Error checking end time:", error);
      throw error;
    }
  };

  // Fixed function for checking delivery slot name uniqueness
  const checkDeliverySlotName = async (
    deliverySlotId: number,
    deliverySlotName: string,
  ) => {
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/deliveryslot/checkDeliverySlotName`,
        "POST",
        {
          deliverySlotId: deliverySlotId || 0,
          deliverySlotName: deliverySlotName.trim(),
        },
        false,
      );

      // console.log("🔍 Raw API response:", response);

      // Handle different response structures
      if (response.data) {
        return response.data;
      } else if (response.response && response.response.data) {
        return response.response.data;
      } else {
        return response;
      }
    } catch (error: any) {
      console.error("Error checking delivery slot name:", error);
      // If it's a validation error (like duplicate name), return the error response
      if (error.response && error.response.data) {
        return error.response.data;
      }
      throw error;
    }
  };

  // Validation functions
  const validateStartTime = async (
    startTime: string,
    deliverySlotId: number,
  ) => {
    if (!startTime) {
      setTimeValidation((prev) => ({ ...prev, startTimeError: "" }));
      return;
    }

    setTimeValidation((prev) => ({ ...prev, validating: true }));

    try {
      const response = await checkStartTime(
        deliverySlotId,
        startTime,
        deliverySlotId ? "1" : "0",
      );

      if (response.status) {
        setTimeValidation((prev) => ({ ...prev, startTimeError: "" }));
      } else {
        setTimeValidation((prev) => ({
          ...prev,
          startTimeError: response.message || "Invalid start time",
        }));
      }
    } catch (error: any) {
      console.error("Start time validation error:", error);
      setTimeValidation((prev) => ({
        ...prev,
        startTimeError:
          error.response?.data?.message || "Error validating start time",
      }));
    } finally {
      setTimeValidation((prev) => ({ ...prev, validating: false }));
    }
  };

  const validateEndTime = async (endTime: string, deliverySlotId: number) => {
    if (!endTime) {
      setTimeValidation((prev) => ({ ...prev, endTimeError: "" }));
      return;
    }

    setTimeValidation((prev) => ({ ...prev, validating: true }));

    try {
      const response = await checkEndTime(
        deliverySlotId,
        endTime,
        deliverySlotId ? "1" : "0",
      );

      if (response.status) {
        setTimeValidation((prev) => ({ ...prev, endTimeError: "" }));
      } else {
        setTimeValidation((prev) => ({
          ...prev,
          endTimeError: response.message || "Invalid end time",
        }));
      }
    } catch (error: any) {
      console.error("End time validation error:", error);
      setTimeValidation((prev) => ({
        ...prev,
        endTimeError:
          error.response?.data?.message || "Error validating end time",
      }));
    } finally {
      setTimeValidation((prev) => ({ ...prev, validating: false }));
    }
  };

  // Fixed validation function for delivery slot name (internal function)
  const validateDeliverySlotNameInternal = async (
    deliverySlotName: string,
    deliverySlotId: number,
  ) => {
    if (!deliverySlotName.trim()) {
      setTimeValidation((prev) => ({ ...prev, slotNameError: "" }));
      return;
    }

    setTimeValidation((prev) => ({
      ...prev,
      validating: true,
      slotNameError: "",
    }));

    try {
      const response = await checkDeliverySlotName(
        deliverySlotId,
        deliverySlotName,
      );

      // console.log("🔍 Validation response:", response);
      // console.log("🔍 Response status:", response?.status);
      // console.log("🔍 Response message:", response?.message);

      // If status is true, the name is available
      if (response?.status === true) {
        console.log("✅ Slot name is unique");
        setTimeValidation((prev) => ({ ...prev, slotNameError: "" }));
      } else {
        // If status is false or not true, show the error message
        console.log("❌ Slot name already exists");
        const errorMessage =
          response?.message || "Delivery slot name already exists";
        setTimeValidation((prev) => ({
          ...prev,
          slotNameError: errorMessage,
        }));
      }
    } catch (error: any) {
      console.error("🚨 Delivery slot name validation error:", error);
      console.error("🚨 Error response:", error.response);

      // Handle API errors - if it's a 400 error with duplicate message, show it
      if (error.response?.status === 400 && error.response?.data?.message) {
        setTimeValidation((prev) => ({
          ...prev,
          slotNameError: error.response.data.message,
        }));
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error validating delivery slot name";
        setTimeValidation((prev) => ({
          ...prev,
          slotNameError: errorMessage,
        }));
      }
    } finally {
      setTimeValidation((prev) => ({ ...prev, validating: false }));
    }
  };

  // Debounced validation function for delivery slot name - only calls API after user stops typing
  const validateDeliverySlotName = useCallback(
    debounce(async (deliverySlotName: string, deliverySlotId: number) => {
      await validateDeliverySlotNameInternal(deliverySlotName, deliverySlotId);
    }, 500),
    [formData.deliverySlotId],
  );

  const validateTimeOrder = (startTime: string, endTime: string) => {
    if (startTime && endTime && startTime >= endTime) {
      setTimeValidation((prev) => ({
        ...prev,
        endTimeError: "End time must be after start time",
      }));
    } else {
      setTimeValidation((prev) => ({ ...prev, endTimeError: "" }));
    }
  };

  // Fetch user permissions
  const fetchUserPermissions = async (): Promise<boolean> => {
    try {
      setUserPermissions((prev) => ({ ...prev, loading: true, error: null }));

      const response = await axiosFunction(
        `${v2ReactURL}/native/getuserrolepermission`,
        "GET",
        false,
      );

      // console.log("Permission API response:", response.data.response.reportListResponse);

      if (response.data?.status && response.data.response?.reportListResponse) {
        const reportList = response.data.response.reportListResponse;

        if (Array.isArray(reportList)) {
          const deliveryPermissions = reportList.find(
            (item: any) =>
              item.type && item.type.toLowerCase() === "deliveryslot",
          );

          // console.log("Found delivery permissions:", deliveryPermissions);

          if (deliveryPermissions && deliveryPermissions.permissionids) {
            let permissionArray: string[] = [];

            if (typeof deliveryPermissions.permissionids === "string") {
              permissionArray = deliveryPermissions.permissionids
                .split(",")
                .map((id: string) => id.trim());
            } else if (Array.isArray(deliveryPermissions.permissionids)) {
              permissionArray = deliveryPermissions.permissionids.map(
                (id: any) => id.toString(),
              );
            }

            // console.log("Delivery permissions array:", permissionArray);

            const canView = permissionArray.includes("2");

            setUserPermissions({
              loading: false,
              canView: canView,
              canInsert: permissionArray.includes("1"),
              canEdit: permissionArray.includes("3"),
              canDelete: permissionArray.includes("4"),
              error: null,
            });

            return canView;
          } else {
            console.warn(
              "No delivery permissions found in reportListResponse array",
            );
            setUserPermissions({
              loading: false,
              canView: false,
              canInsert: false,
              canEdit: false,
              canDelete: false,
              error: "No delivery permissions found in the response",
            });
            return false;
          }
        } else {
          console.error("reportListResponse is not an array:", reportList);
          setUserPermissions({
            loading: false,
            canView: false,
            canInsert: false,
            canEdit: false,
            canDelete: false,
            error:
              "Invalid response format: reportListResponse is not an array",
          });
          return false;
        }
      } else {
        console.warn("Invalid API response structure");
        setUserPermissions({
          loading: false,
          canView: false,
          canInsert: false,
          canEdit: false,
          canDelete: false,
          error:
            response.data?.message ||
            "Failed to load permissions - invalid response structure",
        });
        return false;
      }
    } catch (error: any) {
      console.error("Error fetching permissions:", error);
      setUserPermissions({
        loading: false,
        canView: false,
        canInsert: false,
        canEdit: false,
        canDelete: false,
        error: "Error fetching permissions from server",
      });
      return false;
    }
  };

  // Fetch delivery slot type
  const fetchDeliverySlotType = async () => {
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/getDeliverySlotType`,
        "GET",
        null,
        false,
      );
      // console.log("Fetched delivery slot type:", res.data.response[0].addValue);
      if (res.data.response[0].addValue) {
        setDeliverySlotType(res.data.response[0].addValue);
      }
    } catch (error) {
      console.error("Error fetching delivery slot type:", error);
      customToastMessage("Error fetching delivery slot type", "error");
      setDeliverySlotType("General");
    }
  };

  const fetchData = async (
    searchValue: string,
    startIndex: number,
    pageLength: number,
  ): Promise<void> => {
    setLoadingData(true);
    const payloadData = {
      searchValue: searchValue,
      length: pageLength,
      start: startIndex,
      draw: 1,
    };

    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/deliverySlot/datatable`,
        "POST",
        payloadData,
      );

      const responseData = res?.response?.data || res?.data || res;

      if (responseData && responseData.response) {
        const deliverySlotData = responseData.response;

        if (deliverySlotData.data && Array.isArray(deliverySlotData.data)) {
          const deliverySlotList = deliverySlotData.data.map(
            (col: DeliverySlotData, index: number) => ({
              ...col,
              index: index + 1,
              checked: false,
            }),
          );

          setPagination(
            deliverySlotData.dataTableMetaDTO || {
              page: 0,
              pages: 1,
              perpage: pageLength,
              total: deliverySlotData.data.length,
            },
          );
          setRows(deliverySlotList);
        }
      } else if (responseData && Array.isArray(responseData.data)) {
        const deliverySlotList = responseData.data.map(
          (col: DeliverySlotData, index: number) => ({
            ...col,
            index: index + 1,
            checked: false,
          }),
        );

        setPagination(
          responseData.dataTableMetaDTO || {
            page: 0,
            pages: 1,
            perpage: pageLength,
            total: responseData.data.length,
          },
        );
        setRows(deliverySlotList);
      } else {
        throw new Error("Invalid API response format");
      }

      setIsLoading(false);
      setLoadingData(false);
    } catch (error: any) {
      console.error("Error fetching delivery slot data:", error);
      handleApiError(error);
    }
  };

  const handleApiError = (error: any) => {
    if (error.code === "ERR_NETWORK" || error.message === "network error") {
      setError({
        code: "502",
        message:
          "Hang tight! Our checkout lanes are undergoing maintenance. We'll be back to serving you real soon!",
      });
    } else if (error.response?.status === 400) {
      setError({
        code: "400",
        message:
          "Oops, your request got lost in translation! Maybe try again with simpler instructions?",
      });
    } else if (error.response?.status === 404) {
      setError({
        code: "404",
        message:
          "Oopsie! Looks like this page went on vacation. Maybe try a different path?",
      });
    } else if (error.response?.status === 500) {
      setError({
        code: "500",
        message:
          "Uh-oh, our server's taking a coffee break. It'll be back to ringing up sales soon!",
      });
    } else {
      setError({
        code: "500",
        message:
          "An unexpected error occurred while fetching delivery slot data.",
      });
    }

    customToastMessage("Error fetching delivery slot details", "error");
    setLoadingData(false);
    setIsLoading(false);
  };

  const handleFormChange = async (
    field: keyof DeliverySlotFormData,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-calculate cut-off time when cutOffHours changes
    if (field === "cutOffHours" && formData.startTime) {
      calculateCutOffTime(value, formData.startTime);
    }
    if (field === "startTime" && formData.cutOffHours > 0) {
      calculateCutOffTime(formData.cutOffHours, value);
    }

    // Validate times when they change
    if (field === "startTime" && value) {
      await validateStartTime(value, formData.deliverySlotId);
    }

    if (field === "endTime" && value) {
      await validateEndTime(value, formData.deliverySlotId);
    }

    // Validate delivery slot name when it changes (debounced - calls API after user stops typing)
    if (field === "deliverySlotName") {
      validateDeliverySlotName(value, formData.deliverySlotId);
    }

    // If both times are set, validate time order
    if (
      (field === "startTime" || field === "endTime") &&
      formData.startTime &&
      formData.endTime
    ) {
      validateTimeOrder(formData.startTime, formData.endTime);
    }
  };

  const handleSaveDeliverySlot = async () => {
    // Set validation state to true to show all errors
    setShowValidation(true);

    // Basic validations
    if (!formData.deliverySlotName.trim()) {
      customToastMessage("Delivery slot name is required", "error");
      return;
    }

    if (!formData.startTime) {
      customToastMessage("Start time is required", "error");
      return;
    }

    if (!formData.endTime) {
      customToastMessage("End time is required", "error");
      return;
    }

    // Additional validations for non-general slots
    if (formData.deliverySlotType !== "General") {
      // Fix: Check if cutOffHours is empty, 0, or negative
      if (formData.cutOffHours === 0 || formData.cutOffHours < 0) {
        customToastMessage(
          "Cut-off hours is required and must be greater than 0",
          "error",
        );
        return;
      }

      if (formData.maximumOrders <= 0) {
        customToastMessage("Maximum orders must be greater than 0", "error");
        return;
      }
    }

    // Validate slot name on submit (ensure it's validated before saving)
    await validateDeliverySlotNameInternal(
      formData.deliverySlotName,
      formData.deliverySlotId,
    );

    // Wait a bit for validation to complete if it's still validating
    if (timeValidation.validating) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Check for validation errors
    if (timeValidation.slotNameError) {
      customToastMessage(timeValidation.slotNameError, "error");
      return;
    }

    if (timeValidation.startTimeError) {
      customToastMessage(timeValidation.startTimeError, "error");
      return;
    }

    if (timeValidation.endTimeError) {
      customToastMessage(timeValidation.endTimeError, "error");
      return;
    }

    // Validate time logic
    if (formData.startTime >= formData.endTime) {
      customToastMessage("End time must be after start time", "error");
      return;
    }

    // If all validations pass, proceed with saving
    setSaving(true);
    try {
      const payload = {
        deliverySlotId: formData.deliverySlotId,
        deliverySlotName: formData.deliverySlotName.trim(),
        deliverySlotType: formData.deliverySlotType,
        startTime: formData.startTime,
        endTime: formData.endTime,
        cutOffHours: formData.cutOffHours,
        maximumOrders: formData.maximumOrders,
        calculatedCutOffTime: formData.calculatedCutOffTime,
        isActive: formData.isActive,
      };

      const res = await axiosFunction(
        `${v2ReactURL}/native/saveDeliverySlot`,
        "POST",
        payload,
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage(
          `Delivery slot ${formData.deliverySlotId ? "updated" : "created"} successfully!`,
          "success",
        );
        setOpenCreateModal(false);
        resetForm();
        handleFetchData();
      } else {
        customToastMessage(
          res.data?.message ||
            `Failed to ${formData.deliverySlotId ? "update" : "create"} delivery slot`,
          "error",
        );
      }
    } catch (error: any) {
      console.error("Error saving delivery slot:", error);
      customToastMessage(
        `Error ${formData.deliverySlotId ? "updating" : "creating"} delivery slot`,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete delivery slot function
  const handleDeleteDeliverySlot = async (deliverySlotId: number) => {
    // if (!window.confirm('Are you sure you want to delete this delivery slot?')) {
    //   return;
    // }

    setDeleting(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/deliverySlot/${deliverySlotId}/delete`,
        "POST",
        {},
        false,
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage("Delivery slot deleted successfully!", "success");
        handleFetchData(); // Refresh the data
      } else {
        customToastMessage(
          res.data?.message || "Failed to delete delivery slot",
          "error",
        );
      }
    } catch (error: any) {
      console.error("Error deleting delivery slot:", error);
      customToastMessage("Error deleting delivery slot", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleEditDeliverySlot = (row: DeliverySlotData) => {
    setSelectedDeliverySlot(row);
    setFormData({
      deliverySlotId: row.deliverySlotId,
      deliverySlotName: row.deliverySlotName,
      deliverySlotType: row.deliverySlotType,
      startTime: row.startTime,
      endTime: row.endTime,
      cutOffHours: row.cutOffHours,
      maximumOrders: row.maximumOrders,
      calculatedCutOffTime: row.calculatedCutOffTime,
      isActive: row.isActive,
    });
    setShowValidation(false);
    setTimeValidation({
      startTimeError: "",
      endTimeError: "",
      slotNameError: "",
      validating: false,
    });
    setOpenCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      deliverySlotId: 0,
      deliverySlotName: "",
      deliverySlotType: deliverySlotType,
      startTime: "",
      endTime: "",
      cutOffHours: 0,
      maximumOrders: 0,
      calculatedCutOffTime: "",
      isActive: 1,
    });
    setSelectedDeliverySlot(null);
    setShowValidation(false);
    setTimeValidation({
      startTimeError: "",
      endTimeError: "",
      slotNameError: "",
      validating: false,
    });
  };

  const calculateCutOffTime = (cutOffHours: number, startTime: string) => {
    if (!startTime) return;

    const parts = startTime.split(":");
    const hours = Number(parts[0] ?? 0);
    const minutes = Number(parts[1] ?? 0);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);

    const cutOffTime = new Date(
      startDate.getTime() - cutOffHours * 60 * 60 * 1000,
    );

    const cutOffHoursStr = cutOffTime.getHours().toString().padStart(2, "0");
    const cutOffMinutesStr = cutOffTime
      .getMinutes()
      .toString()
      .padStart(2, "0");

    setFormData((prev) => ({
      ...prev,
      calculatedCutOffTime: `${cutOffHoursStr}:${cutOffMinutesStr}`,
    }));
  };

  const handleFetchData = async (): Promise<void> => {
    await fetchData(
      searchValue,
      pagination.page * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  useEffect(() => {
    // Initialize data with proper permission checking
    const initializeData = async () => {
      const hasViewPermission = await fetchUserPermissions();

      if (hasViewPermission) {
        await fetchDeliverySlotType();
        await handleFetchData();
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    // Update form data when deliverySlotType changes
    if (!formData.deliverySlotId) {
      // Only update for new slots
      setFormData((prev) => ({
        ...prev,
        deliverySlotType: deliverySlotType,
      }));
    }
  }, [deliverySlotType]);

  const handleChangePageOption = (e: { target: { value: any } }) => {
    const selectedValue = e.target.value;
    setPageOption(parseInt(selectedValue).toString());
    fetchData(searchValue, 0, parseInt(selectedValue));
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchData(
      searchValue,
      (page - 1) * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  const handleSearchData = useCallback(
    debounce((searchValue: string) => {
      fetchData(searchValue, 0, parseInt(pageOption));
    }, 500),
    [pageOption],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    handleSearchData(value);
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return "N/A";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours || "0");
      const ampm = hour >= 12 ? "PM" : "AM";
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const columnsDataTableDeliverySlot: Column[] = [
    {
      field: "srNo",
      headerName: "Sr. No",
      width: 5,
      defaultChecked: true,
      renderCell: (params: any) => (
        <Typography variant="body2" color="textSecondary">
          {pagination.page * pagination.perpage + params.row.index}
        </Typography>
      ),
    },
    {
      field: "deliverySlotName",
      headerName: "Delivery Slot Name",
      width: 80,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500" color="text.primary">
          {params.row.deliverySlotName}
        </Typography>
      ),
    },
    {
      field: "timeRange",
      headerName: "Time Range",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatTime(params.row.startTime)} - {formatTime(params.row.endTime)}
        </Typography>
      ),
    },

    // Conditionally add columns if deliverySlotType is NOT 'General'
    ...(deliverySlotType !== "General"
      ? [
          {
            field: "maximumOrders",
            headerName: "Max Orders",
            width: 60,
            defaultChecked: true,
            renderCell: (params: any) => (
              <Typography variant="body2" fontWeight="500">
                {params.row.maximumOrders}
              </Typography>
            ),
          },
          {
            field: "cutOffTime",
            headerName: "Cut-off Time",
            width: 80,
            defaultChecked: true,
            renderCell: (params: any) => (
              <Typography variant="body2">
                {formatTime(params.row.calculatedCutOffTime)}
              </Typography>
            ),
          },
        ]
      : []),

    {
      field: "status",
      headerName: "Status",
      width: 60,
      defaultChecked: true,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive === 1 ? "Active" : "Inactive"}
          size="small"
          color={params.row.isActive === 1 ? "success" : "default"}
        />
      ),
    },
    {
      field: "Action",
      headerName: "Actions",
      width: 130,
      defaultChecked: true,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {userPermissions.canEdit && (
            <IconButton
              size="small"
              onClick={() => handleEditDeliverySlot(params.row)}
              sx={{
                color: "warning.main",
                "&:hover": { backgroundColor: "warning.light", color: "white" },
              }}
            >
              <FaEdit />
            </IconButton>
          )}
          {userPermissions.canDelete && (
            <IconButton
              size="small"
              onClick={() =>
                handleDeleteDeliverySlot(params.row.deliverySlotId)
              }
              disabled={deleting}
              sx={{
                color: "error.main",
                "&:hover": { backgroundColor: "error.light", color: "white" },
              }}
            >
              {deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleOpenModal = (row: DeliverySlotData) => {
    setSelectedDeliverySlot(row);
    setOpenModal(true);
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    resetForm();
  };

  const toggleFilter = (column: Column) => {
    setSearchInputValue("");
    if (openFilter === column.field) {
      setOpenFilter(null);
    } else {
      setOpenFilter(column.field);
    }
  };

  const handleInputFocus = (field: string) => {
    setOpenFilter(field);
  };

  const handleApplyFilterBtn = () => {
    // Filter implementation if needed
  };

  // Keep references to avoid TS errors
  void selectedDeliverySlot;
  void openModal;
  void handleOpenModal;

  // Show permission loading
  if (userPermissions.loading) {
    return <Loader />;
  }

  // Check view permission
  if (!userPermissions.canView) {
    return (
      <ErrorPage
        error={{
          code: "403",
          message: "You do not have permission to view delivery slots.",
        }}
      />
    );
  }

  if (tabView === "") {
    return (
      <ErrorPage
        error={{
          code: "403",
          message:
            "Whoops, access denied! Seems like you hit a dead end. Mysteries of the web, right?",
        }}
      />
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>{`${navUrl} - ${deliverySlotType}`}</title>
      </Helmet>
      <Wrapper navUrl={navUrl} customStyle={{ height: "750px" }}>
        <WrapperSubHeader className="mainBreadcrumb">
          <div className="mr-auto">
            <SubHeaderTitle subTitle={`${navUrl} - ${deliverySlotType}`} />
          </div>
        </WrapperSubHeader>
        <WrapperContent>
          <Row>
            <WrapperCard isCollapsableAnalytics={false}>
              <CustomTabPanel>
                {/* Analytics section can be added here if needed */}
              </CustomTabPanel>

              {/* Compact Header Section */}
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
                sx={{ px: 2, pt: "10px" }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    placeholder="Search delivery slots..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                      ),
                    }}
                    sx={{ width: 250 }}
                  />
                  {/* Create button - only show if user has insert permission */}
                  {userPermissions.canInsert && (
                    <CustomButton
                      onClick={() => {
                        resetForm();
                        setOpenCreateModal(true);
                      }}
                      btnName={"Create New"}
                      variant="contained"
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              </Box>

              {/* Compact Create/Edit Delivery Slot Modal */}
              <Dialog
                open={openCreateModal}
                onClose={handleCloseCreateModal}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    maxWidth: 600,
                  },
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="h6" fontWeight="600">
                      {formData.deliverySlotId
                        ? "Edit Delivery Slot"
                        : "Create Delivery Slot"}
                    </Typography>
                    <IconButton onClick={handleCloseCreateModal} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    {/* Delivery Slot Name - Always shown */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="600"
                        gutterBottom
                      >
                        Delivery Slot Name *
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.deliverySlotName}
                        onChange={(e) =>
                          handleFormChange("deliverySlotName", e.target.value)
                        }
                        placeholder="Enter delivery slot name"
                        required
                        error={showValidation && !formData.deliverySlotName}
                        helperText={
                          showValidation && !formData.deliverySlotName
                            ? "Delivery slot name is required"
                            : timeValidation.slotNameError &&
                              timeValidation.slotNameError
                        }
                        InputProps={{
                          endAdornment: timeValidation.validating ? (
                            <CircularProgress size={16} />
                          ) : null,
                        }}
                      />
                    </Grid>

                    {/* Time Range Section - Always shown */}
                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="600"
                        gutterBottom
                      >
                        Start Time *
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="time"
                        value={formData.startTime}
                        onChange={(e) =>
                          handleFormChange("startTime", e.target.value)
                        }
                        required
                        error={
                          (showValidation && !formData.startTime) ||
                          !!timeValidation.startTimeError
                        }
                        helperText={
                          showValidation && !formData.startTime
                            ? "Start time is required"
                            : timeValidation.startTimeError ||
                              "Enter start time"
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          endAdornment: timeValidation.validating ? (
                            <CircularProgress size={16} />
                          ) : null,
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="600"
                        gutterBottom
                      >
                        End Time *
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="time"
                        value={formData.endTime}
                        onChange={(e) =>
                          handleFormChange("endTime", e.target.value)
                        }
                        required
                        error={
                          (showValidation && !formData.endTime) ||
                          !!timeValidation.endTimeError
                        }
                        helperText={
                          showValidation && !formData.endTime
                            ? "End time is required"
                            : timeValidation.endTimeError || "Enter end time"
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          endAdornment: timeValidation.validating ? (
                            <CircularProgress size={16} />
                          ) : null,
                        }}
                      />
                    </Grid>

                    {/* Additional Fields - Only show when NOT General */}
                    {formData.deliverySlotType !== "General" && (
                      <>
                        {/* Cut-off Hours and Maximum Orders */}
                        <Grid item xs={6}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="600"
                            gutterBottom
                          >
                            Cut-off Hours *
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={
                              formData.cutOffHours === 0
                                ? ""
                                : formData.cutOffHours
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty value but store as 0
                              if (value === "") {
                                setFormData((prev) => ({
                                  ...prev,
                                  cutOffHours: 0,
                                }));
                              } else {
                                const numValue = parseInt(value);
                                if (!isNaN(numValue) && numValue >= 0) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    cutOffHours: numValue,
                                  }));
                                  if (formData.startTime) {
                                    calculateCutOffTime(
                                      numValue,
                                      formData.startTime,
                                    );
                                  }
                                }
                              }
                            }}
                            placeholder="0"
                            required
                            error={
                              showValidation &&
                              (formData.cutOffHours === 0 ||
                                formData.cutOffHours < 0)
                            }
                            helperText={
                              showValidation &&
                              (formData.cutOffHours === 0 ||
                                formData.cutOffHours < 0)
                                ? "Cut-off hours is required and must be greater than 0"
                                : ""
                            }
                            inputProps={{
                              min: 1, // Changed from 0 to 1 to enforce positive values
                              onKeyDown: (e) => {
                                if (
                                  e.key === "-" ||
                                  e.key === "e" ||
                                  e.key === "E"
                                ) {
                                  e.preventDefault();
                                }
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="600"
                            gutterBottom
                          >
                            Maximum Orders *
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={
                              formData.maximumOrders === 0
                                ? ""
                                : formData.maximumOrders
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty value but store as 0
                              if (value === "") {
                                setFormData((prev) => ({
                                  ...prev,
                                  maximumOrders: 0,
                                }));
                              } else {
                                const numValue = parseInt(value);
                                if (!isNaN(numValue) && numValue >= 0) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    maximumOrders: numValue,
                                  }));
                                }
                              }
                            }}
                            placeholder="0"
                            required
                            error={
                              showValidation && formData.maximumOrders <= 0
                            }
                            helperText={
                              showValidation && formData.maximumOrders <= 0
                                ? "Maximum orders must be greater than 0"
                                : ""
                            }
                            inputProps={{
                              min: 1,
                              onKeyDown: (e) => {
                                if (
                                  e.key === "-" ||
                                  e.key === "e" ||
                                  e.key === "E"
                                ) {
                                  e.preventDefault();
                                }
                              },
                            }}
                          />
                        </Grid>

                        {/* Calculated Cut-off Time - Read only */}
                        {formData.calculatedCutOffTime && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              fontWeight="600"
                              gutterBottom
                            >
                              Calculated Cut-off Time
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              value={formatTime(formData.calculatedCutOffTime)}
                              InputProps={{
                                readOnly: true,
                              }}
                              variant="outlined"
                              helperText="Automatically calculated based on start time and cut-off hours"
                            />
                          </Grid>
                        )}

                        {/* Status */}
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={formData.isActive === 1}
                                onChange={(e) =>
                                  handleFormChange(
                                    "isActive",
                                    e.target.checked ? 1 : 0,
                                  )
                                }
                                color="primary"
                              />
                            }
                            label={
                              <Typography variant="subtitle2" fontWeight="600">
                                Active
                              </Typography>
                            }
                          />
                        </Grid>
                      </>
                    )}

                    {/* Action Buttons - Always shown */}
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={1}
                      >
                        <Button
                          size="small"
                          onClick={handleCloseCreateModal}
                          disabled={saving || timeValidation.validating}
                          variant="outlined"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveDeliverySlot}
                          disabled={
                            saving ||
                            timeValidation.validating ||
                            !!timeValidation.slotNameError
                          }
                          startIcon={
                            saving ? <CircularProgress size={16} /> : null
                          }
                        >
                          {saving
                            ? "Saving..."
                            : formData.deliverySlotId
                              ? "Update"
                              : "Save"}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </DialogContent>
              </Dialog>

              {/* Data Table */}
              {loadingData ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <CustomDataTable
                  rows={rows}
                  columns={columnsDataTableDeliverySlot}
                  paginationInfo={pagination}
                  handlePageChange={handlePageChange}
                  isCheckboxShow={false}
                  FixedTable={true}
                  pageOption={pageOption}
                  handleChangePageOption={handleChangePageOption}
                  loading={loadingData}
                  isBranchFieldExist={false}
                  setCheckedValue={setCheckedValue}
                  checkedValue={checkedValue}
                  handleApplyFilterBtn={handleApplyFilterBtn}
                  toggleFilter={toggleFilter}
                  openFilter={openFilter}
                  setOpenFilter={setOpenFilter}
                  handleInputFocus={handleInputFocus}
                  branchLocationList={[]}
                  filterData={[]}
                  setFilterData={() => {}}
                  handleSearch={() => {}}
                  contactFilteredIds={[]}
                  setContactFilteredIds={() => {}}
                  filteringData={{}}
                  setSearchInputValue={setSearchInputValue}
                  searchInputValue={searchInputValue}
                  linkField={"deliverySlotName"}
                />
              )}
            </WrapperCard>
          </Row>
        </WrapperContent>
      </Wrapper>

      {/* Snackbar for notifications - BOTTOM RIGHT */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: snackbar.vertical,
          horizontal: snackbar.horizontal,
        }}
        sx={{
          "& .MuiAlert-root": {
            fontSize: "14px",
            fontWeight: 500,
          },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            "&.MuiAlert-filledSuccess": {
              backgroundColor: "#4caf50",
            },
            "&.MuiAlert-filledError": {
              backgroundColor: "#f44336",
            },
            "&.MuiAlert-filledWarning": {
              backgroundColor: "#ff9800",
            },
            "&.MuiAlert-filledInfo": {
              backgroundColor: "#2196f3",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeliverySlot;
